/**
 * /logs left-pane Time / + / − / Cash chips. State lives in Redux so the
 * table, Reporting, and Metrics stay in sync.
 */
import React from 'react';
import DateRange from '@mui/icons-material/DateRange';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import NumbersOutlined from '@mui/icons-material/NumbersOutlined';
import { useLocation } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { ModalParts } from 'pages/redux';
import { isErpPagePath } from 'pages/Layout/erpModulePaths';
import TimeRangeModal from '../modals/TimeRangeModal';
import * as S from './ActivitySelector.styled';
import {
  ACTIVITY_LOGS_REPORT_LOGS_FOCUS_FILTERS_TOOLTIP,
  selectActivityLogsAllItems,
  selectActivityLogsCashIn,
  selectActivityLogsCashOut,
  selectActivityLogsCombineSku,
  selectActivityLogsUseTimeRange,
  selectIsActivityLogsReportLogsFocus,
  selectLogsValueMode,
  selectSingleLogTime,
  setAssociateElevatedActivityLogs,
  setActivityLogsCashFilters,
  setActivityLogsCombineSku,
  setActivityLogsStartEndLogIds,
  setActivityLogsUseTimeRange,
  setActivityLogsViewAs,
  setComplianceActivity,
  setSingleLogTime,
} from '../redux';
import { useActivityLogsDisplayLogs } from '../useActivityLogsDisplay';

type ActivityLogsTableFiltersProps = {
  setModal: (modal: ModalParts | null) => void,
  /** Expanded pane: Combine SKUs sits at the right of this row, next to Cash. */
  inlineCombineSku?: boolean,
  /** Search prompt: pane is wide — use the longer Time / Cash labels. */
  widePane?: boolean,
  /** Results bar / Search activity card — no left-pane bleed margins. */
  inline?: boolean,
};

const CASH_ONLY_LOGS_TOOLTIP = 'Show only logs that have a cash amount. Logs with no money value are hidden.';
const UNITS_ONLY_LOGS_TOOLTIP = 'Show only logs that have a unit count. Logs with no units are hidden.';

const METRICS_TIME_RANGE_LOCKED_TOOLTIP = 'Time range is set on Metrics.';
const METRICS_TIME_RANGE_LOCKED_TOOLTIP_ID = 'activityLogsFiltersTimeLockedOnMetrics';

const ActivityLogsTableFilters = ({
  setModal,
  inlineCombineSku,
  widePane,
  inline,
}: ActivityLogsTableFiltersProps) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  // /metrics owns the date range (MetricsTime). Keep Time visible but locked.
  const timeRangeLockedOnMetrics = isErpPagePath(location.pathname, 'metrics');
  const isUnitsMode = useAppSelector(selectLogsValueMode) === 'units';
  const allItems = useAppSelector(selectActivityLogsAllItems);
  const cashIn = useAppSelector(selectActivityLogsCashIn);
  const cashOut = useAppSelector(selectActivityLogsCashOut);
  // +/− lit state follows Redux only — SKU picks (e.g. VO) must not dim them.
  // hasCashLogs only blocks entering Cash mode when the selection has no cash rows.
  const { displayLogs: cashOnlyLogs } = useActivityLogsDisplayLogs({
    allItems: false,
    cashIn: true,
    cashOut: true,
  });
  const hasCashLogs = cashOnlyLogs.length > 0;
  const activityLogsCombineSku = useAppSelector(selectActivityLogsCombineSku);
  const activityLogsUseTimeRange = useAppSelector(selectActivityLogsUseTimeRange);
  const singleLogTime = useAppSelector(selectSingleLogTime);
  const isReportLogsFocus = useAppSelector(selectIsActivityLogsReportLogsFocus);
  // Time chip is calendar-only — activity-log lock does not turn this on.
  const timeRangeControlOn = Boolean(
    activityLogsUseTimeRange
    || (singleLogTime?.startDate && singleLogTime?.endDate),
  );
  const filtersLocked = isReportLogsFocus;
  const filtersLockTitle = filtersLocked
    ? ACTIVITY_LOGS_REPORT_LOGS_FOCUS_FILTERS_TOOLTIP
    : undefined;
  const timeRangeDisabled = filtersLocked || activityLogsCombineSku || timeRangeLockedOnMetrics;
  const timeRangeTitle = timeRangeLockedOnMetrics
    ? undefined
    : (filtersLockTitle
      || (activityLogsCombineSku ? 'Turn off Combine SKUs to use time range.' : 'Time range'));

  const openTimeRangeModal = () => {
    if (timeRangeDisabled) return;
    setModal({
      component: (
        <TimeRangeModal
          isOn={timeRangeControlOn}
          onApply={({ isOn }) => {
            dispatch(setActivityLogsUseTimeRange(isOn));
            if (!isOn) {
              dispatch(setSingleLogTime(null));
              dispatch(setAssociateElevatedActivityLogs(false));
              dispatch(setComplianceActivity({ active: false }));
              dispatch(setActivityLogsViewAs(null));
            }
          }}
          onExit={() => setModal(null)}
        />
      ),
      exitOnBgClick: () => setModal(null),
    });
  };

  return (
    <S.TableFilterRow $inline={inline}>
      <S.TableFilterChipWrap
        $disabled={timeRangeDisabled}
        title={timeRangeTitle}
        data-tooltip-id={timeRangeLockedOnMetrics
          ? METRICS_TIME_RANGE_LOCKED_TOOLTIP_ID : undefined}
        data-tooltip-content={timeRangeLockedOnMetrics
          ? METRICS_TIME_RANGE_LOCKED_TOOLTIP : undefined}
      >
        <S.TableFilterChip
          type="button"
          $on={timeRangeControlOn && !activityLogsCombineSku && !timeRangeLockedOnMetrics}
          disabled={timeRangeDisabled}
          aria-disabled={timeRangeDisabled}
          data-analytics="activityLogs_filter_time"
          onClick={openTimeRangeModal}
        >
          <DateRange sx={{ fontSize: 15 }} />
          {widePane ? 'Timerange' : 'Time'}
        </S.TableFilterChip>
      </S.TableFilterChipWrap>
      {timeRangeLockedOnMetrics ? (
        <Tooltip
          id={METRICS_TIME_RANGE_LOCKED_TOOLTIP_ID}
          place="bottom"
          positionStrategy="fixed"
          delayShow={400}
          closeEvents={{ click: true, mouseleave: true, blur: true }}
        />
      ) : null}
      {!isUnitsMode && (
        <>
          <S.TableFilterChipWrap $disabled={filtersLocked} title={filtersLockTitle || 'Cash in'}>
            <S.TableFilterChip
              type="button"
              $on={cashIn}
              disabled={filtersLocked}
              data-analytics="activityLogs_filter_cash_in"
              onClick={() => {
                // No cash rows — don't turn Cash on or drop −.
                if (allItems && !hasCashLogs) return;
                if (allItems) {
                  dispatch(setActivityLogsCashFilters({
                    allItems: false,
                    cashIn: true,
                    cashOut: false,
                  }));
                  return;
                }
                dispatch(setActivityLogsCashFilters({
                  cashIn: !cashIn,
                  ...(cashIn ? { cashOut: true } : {}),
                }));
              }}
            >
              <S.TableFilterSign>+</S.TableFilterSign>
            </S.TableFilterChip>
          </S.TableFilterChipWrap>
          <S.TableFilterChipWrap $disabled={filtersLocked} title={filtersLockTitle || 'Cash out'}>
            <S.TableFilterChip
              type="button"
              $on={cashOut}
              $danger
              disabled={filtersLocked}
              data-analytics="activityLogs_filter_cash_out"
              onClick={() => {
                // No cash rows — don't turn Cash on or drop +.
                if (allItems && !hasCashLogs) return;
                if (allItems) {
                  dispatch(setActivityLogsCashFilters({
                    allItems: false,
                    cashOut: true,
                    cashIn: false,
                  }));
                  return;
                }
                dispatch(setActivityLogsCashFilters({
                  cashOut: !cashOut,
                  ...(cashOut ? { cashIn: true } : {}),
                }));
              }}
            >
              <S.TableFilterSign>−</S.TableFilterSign>
            </S.TableFilterChip>
          </S.TableFilterChipWrap>
        </>
      )}
      <S.TableFilterChipWrap
        $disabled={filtersLocked}
        title={filtersLockTitle || (isUnitsMode ? UNITS_ONLY_LOGS_TOOLTIP : CASH_ONLY_LOGS_TOOLTIP)}
      >
        <S.TableFilterChip
          type="button"
          $on={!allItems}
          disabled={filtersLocked}
          data-analytics="activityLogs_filter_cash_only"
          onClick={() => {
            const nextAllItems = !allItems;
            dispatch(setActivityLogsCashFilters({
              allItems: nextAllItems,
              ...(!nextAllItems && !isUnitsMode
                ? { cashIn: true, cashOut: true }
                : {}),
            }));
          }}
        >
          {isUnitsMode
            ? <NumbersOutlined sx={{ fontSize: 15 }} />
            : <PaymentsOutlined sx={{ fontSize: 15 }} />}
          {isUnitsMode ? 'Units' : (widePane ? 'Cash only logs' : 'Cash')}
        </S.TableFilterChip>
      </S.TableFilterChipWrap>
      {inlineCombineSku && !isUnitsMode ? (
        <S.CombineSkuToggle
          type="button"
          $inline
          title="Roll up rows with cash values by log type (SKU). Empty or N/A values stay separate."
          onClick={() => {
            const nextOn = !activityLogsCombineSku;
            dispatch(setActivityLogsCombineSku(nextOn));
            // Combine SKUs cannot pin a single activity log — drop the lock.
            if (nextOn) dispatch(setActivityLogsStartEndLogIds(null));
          }}
        >
          <S.CombineSkuCheck
            type="checkbox"
            checked={activityLogsCombineSku}
            readOnly
            tabIndex={-1}
          />
          Combine SKUs
        </S.CombineSkuToggle>
      ) : null}
    </S.TableFilterRow>
  );
};

export default ActivityLogsTableFilters;
